import type { ITask } from "../types";

const STALE_DAYS = 2;
const COOLDOWN_HOURS = 4;

/**
 * A task is "stale" and should appear in Triage when:
 *  - it's active (backlog/today, not deleted, not completed),
 *  - older than STALE_DAYS,
 *  - not currently snoozed,
 *  - not nudged within the last COOLDOWN_HOURS (so "Keep" hides it briefly).
 *
 * Matches the server-side candidate filter in send-nudges so Triage and push
 * notifications stay consistent.
 */
export function isStale(task: ITask, now: Date = new Date()): boolean {
  if (task.deleted_at) return false;
  if (task.completed_at) return false;
  if (task.status !== "backlog" && task.status !== "today") return false;

  const age = now.getTime() - new Date(task.created_at).getTime();
  if (age < STALE_DAYS * 24 * 60 * 60 * 1000) return false;

  if (task.snooze_until && new Date(task.snooze_until).getTime() > now.getTime()) return false;

  if (task.last_nudged_at) {
    const cooldownMs = COOLDOWN_HOURS * 60 * 60 * 1000;
    if (now.getTime() - new Date(task.last_nudged_at).getTime() < cooldownMs) return false;
  }

  return true;
}

export function selectStaleTasks(tasks: ITask[], now: Date = new Date()): ITask[] {
  return tasks
    .filter((t) => isStale(t, now))
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}
